import { useCallback } from 'react';
import {
  useSnackbar,
  useTable,
  useTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import { useSearchParams } from 'react-router-dom';
import {
  IGetGroupRestrictionsParams,
  TGetGroupRestrictionsMethod,
  TRestriction,
} from '../../useGroupRestrictionsAPI/interfaces';
import useGroupRestrictionsAPI from '../../useGroupRestrictionsAPI';
import { TGroupRestrictionsTableRow } from '../../../components/tables/GroupRestrictionsTable';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';

interface IPersistentFilters extends Pick<IGetGroupRestrictionsParams, 'groupId'> {}

export interface IUseGroupRestrictionsTableProps {
  persistentFilters: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUseGroupRestrictionsTableReturn extends
  Omit<
    IUseTableReturn<TGroupRestrictionsTableRow>,
    'columnOrderBy' | 'columnOrderDirection' | 'onColumnOrderByChange'
    | 'onColumnOrderDirectionChange' | 'selectedRows' | 'resetTableMethods'
  >,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseTableFetchReturn<TRestriction>, 'data'>
{
  restrictions: IUseTableFetchReturn<TRestriction>['data'];
  selectedRestrictions: IUseTableReturn<TGroupRestrictionsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

interface ITablePreservationConfigs extends
  Omit<IGetGroupRestrictionsParams, 'page' | 'pageLength' | 'groupId' | 'cancelToken'>
{
  activePage: IGetGroupRestrictionsParams['page'];
  perPage: IGetGroupRestrictionsParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
};

const useGroupRestrictionsTable = (
  props: IUseGroupRestrictionsTableProps,
): IUseGroupRestrictionsTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = props;

  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getGroupRestrictions } = useGroupRestrictionsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const {
    activePage,
    perPage,
    selectedRows: selectedRestrictions,
    resetTableMethods,
    ...restTableProps
  } = useTable<TGroupRestrictionsTableRow>({
    initialActivePage,
    initialPerPage,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: restrictions, isLoading, isFetchedAfterMount, total, refetch,
  } = useTableFetch<
    IGetGroupRestrictionsParams,
    TGetGroupRestrictionsMethod,
    TRestriction
  >({
    request: getGroupRestrictions,
    params: {
      page: activePage,
      pageLength: perPage,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
  });

  return {
    restrictions,
    isLoading,
    total,
    refetch,
    activePage,
    perPage,
    selectedRestrictions,
    localization,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default useGroupRestrictionsTable;
